/* eslint-disable radix */
import React from 'react';
import { InputLabel } from '@mui/material';
import {
  Controller, useController, Path, PathValue, FieldValues,
} from 'react-hook-form';
import { usePatternFormat, NumberFormatBase } from 'react-number-format';
import { twMerge } from 'tailwind-merge';
import ElementUtils from '../utils';
import { InputProps } from '.';
import { CommonReactHookFormProps } from '../types';

interface RHFDOBInputProps<T,
  Value, FormFields extends FieldValues,
> extends InputProps<T, Value>, CommonReactHookFormProps<FormFields> { }

function RHFDOBInput<T, Value, FormFields extends FieldValues>(
  props: RHFDOBInputProps<T, Value, FormFields>,
) {
  const {
    name,
    control,
    value,
    label,
    disabled,
    helperText,
  } = props;

  const {
    fieldState: { error },
  } = useController<FormFields>({
    name,
    control,
    rules: { required: true },
    defaultValue: value as PathValue<FormFields, Path<FormFields>> | undefined,
  });
  const { format } = usePatternFormat({ ...{}, format: '##/##' });

  const dateFormatter = (val: string) => {
    let month = val.substring(0, 2);
    let day = val.substring(2, 4);

    if (day.length === 1 && day && day.length > 0 && parseInt(day[0]) > 3) {
      day = `0${day[0]}`;
    } else if (day.length === 2) {
      // set the lower and upper boundary
      if (Number(day) === 0) {
        day = '01';
      } else if (Number(day) > 31) {
        day = '31';
      }
    }

    if (month.length === 1 && month && month.length > 0 && parseInt(month[0]) > 1) {
      month = `0${month[0]}`;
    } else if (month.length === 2) {
      // set the lower and upper boundary
      if (Number(month) === 0) {
        month = '01';
      } else if (Number(month) > 12) {
        month = '12';
      }
    }

    return format?.(`${month}${day}`) || '';
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <div className="relative flex items-start justify-center flex-col">
          <InputLabel className={twMerge('flex font-bold text-[#28364C]')} error={!!error}>
            {label}
          </InputLabel>
          <NumberFormatBase
            {...field}
            format={dateFormatter}
            placeholder={helperText}
            onValueChange={(values: any) => {
              field.onChange(values.value);
            }}
            className=" outline outline-[1px] outline-error placeholder-secondary/60 placeholder:text-sm border-0  bg-transparent h-[1.4375em] m-0 block min-w-0 py-[18px] px-[14px] w-full bg-white custom-shadow rounded-md mt-1 outline-none border-none"
            style={
              {
                outline: error ? 'solid' : 'none',
                outlineWidth: error ? '1px' : '0px',
                outlineColor: error ? 'red' : '',
              }
            }
            disabled={disabled}
          />
          {error && (
            <div className="absolute -bottom-5 text-error text-xs">
              {ElementUtils.renderError(error)}
            </div>
          )}
        </div>
      )}
    />
  );
}

export default RHFDOBInput;
